import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SecurityContext,
} from '@angular/core';
import { AbstractControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Navigation, Router } from '@angular/router';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import { InvalidFieldComponent } from 'src/app/shared/components';
import { PaymentMethodsService } from 'src/app/shared/services/payment-methods.service';

import { MediaMatcher } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import {
    CredentialsTokensType,
    Exception,
    KeycloakException,
    LoginAsCargoSprintUserResponse,
} from '@cargos/sprintpay-models';
import { AuthService, ExternalLinksModeService, StatService } from '@cargos/sprintpay-services';
import {
    catchError,
    distinctUntilChanged,
    EMPTY,
    finalize,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    takeUntil,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { MatStrokedButtonBorderDirective } from '../../directives';
import { TermsOfUseComponent } from '../../modules/terms-of-use/terms-of-use.component';
import { SanitizerPipe } from '../../pipes';
import {
    CartBillService,
    ConfigService,
    CurrentFacilityService,
    CustomerService,
    // LoginAsCargoSprintUserResponse,
    SessionService,
} from '../../services';
import { BannersService } from '../../services/banners.service';
import { KeycloakHandlerService } from '../../services/keycloak/keycloak.service';
import { RouteEventsService } from '../../services/url.service';
import { buildSSORedirectUrl } from '../../utils/constants';
import { TOUNotification } from '../../utils/notifications';
import { LoginFormErrorJSON, LoginFormGroupType } from './models/login-form';
import { formLoginErrors } from './utils/form-errors';
@Component({
    selector: 'app-login-form',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        InvalidFieldComponent,
        MatProgressSpinnerModule,
        MatStrokedButtonBorderDirective,
        SanitizerPipe,
        MatButtonModule,
    ],
    templateUrl: './login-form.component.html',
    styleUrl: './login-form.component.scss',
})
export class LoginFormComponent implements OnInit, OnDestroy {
    public loginForm: LoginFormGroupType;
    public formErrors: LoginFormErrorJSON;
    public hide = true;
    public isLoading = false;
    private navigation: Navigation | null;
    private _mobileQueryListener: () => void;
    public mobileQuery: MediaQueryList;
    public urlNewAccount: string;
    public loginSSOTarget: string = '_self';
    public noSSOLogin = false;
    public previousURL: string;
    private unsubscribe$: Subject<void>;
    private readonly externalLinksModeService = inject(ExternalLinksModeService);

    get password(): AbstractControl<string> | null {
        return this.loginForm.get('password');
    }

    get email(): AbstractControl<string> | null {
        return this.loginForm.get('email');
    }

    @Input() sourceSignUp: string = 'sprintpay';
    @Output() eventOnChangeForgotPW = new EventEmitter<boolean>();

    constructor(
        private fb: NonNullableFormBuilder,
        private router: Router,
        private currentFacilityService: CurrentFacilityService,
        private location: Location,
        private matDialog: MatDialog,
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private sessionService: SessionService,
        private customerService: CustomerService,
        private cartBillService: CartBillService,
        private configService: ConfigService,
        private authService: AuthService,
        private domSanitizer: DomSanitizer,
        private statService: StatService,
        private routeEventsService: RouteEventsService,
        private paymentMethodsService: PaymentMethodsService,
        private bannersService: BannersService,
        private keycloakHandlerService: KeycloakHandlerService
    ) {
        this.formErrors = formLoginErrors;
        this.navigation = this.router.getCurrentNavigation();
        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
        this.unsubscribe$ = new Subject<void>();
        this.previousURL = this.routeEventsService.instant_previous_route_path;
    }

    ngOnInit(): void {
        this.getConfig();
        this.createForm();
        if (environment.projects) {
            this.urlNewAccount = `${environment.projects['sprintPay']}/signup/${this.sourceSignUp}/account-type`;
        }
        this.subscribeMobileSize();
        this.onEmailChange();
        this.handleKeycloakRedirect();
    }

    ngOnDestroy(): void {
        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getConfig(): void {
        this.loginSSOTarget = this.configService.getLoginSSOTarget();
    }

    createForm(): void {
        this.loginForm = this.fb.group({
            email: ['', [Validators.required, CustomValidators.regex(new RegExp(CustomPatterns.EMAIL))]],
            password: [''],
        });
    }

    forgotTo(): void {
        this.eventOnChangeForgotPW.emit(true);
        this.router.navigate(['/recover-password']);
    }

    clickOnLogin(): void {
        if (this.loginForm.invalid || this.isLoading) {
            return;
        }

        this.isLoading = true;

        of(this.noSSOLogin)
            .pipe(
                take(1),
                switchMap((noSSOLogin) => (!noSSOLogin ? this.SSOLogin(this.email?.value!) : this.standardLogin())),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (response: { loginResponse: LoginAsCargoSprintUserResponse; isTermsOfUseAccepted?: boolean }) => {
                    const {
                        loginResponse: { redirectUrl, loginFlow, isMigrated },
                        isTermsOfUseAccepted,
                    } = response;
                    if (loginFlow === 'SSO') {
                        this.validateSSO(redirectUrl, isMigrated);
                    } else {
                        if (isTermsOfUseAccepted) {
                            this.bannersService.setAndSaveTouBanner(TOUNotification);

                            if (this.navigation && this.navigation.previousNavigation !== null) {
                                if (this.previousURL === '/cart/echeck') {
                                    this.router.navigate(['/cart']);
                                } else {
                                    this.location.back();
                                }
                            } else {
                                this.router.navigate(['/']);
                            }
                        } else {
                            this.displaySubscriptionModal();
                        }
                    }
                },
                error: (error: Exception) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'warning',
                    });
                },
            });
    }

    onLogout(): void {
        this.paymentMethodsService.setPaymentMethods([]);
        this.paymentMethodsService
            .getGuestPaymentMethod()
            .pipe(take(1))
            .subscribe((guestMethods) => {
                this.paymentMethodsService.setPaymentMethods(guestMethods);
            });
    }

    isTermsOfUseAccepted(): Observable<boolean> {
        return of(this.customerService.is_terms_of_use_accepted);
    }

    login(): Observable<LoginAsCargoSprintUserResponse> {
        return this.sessionService.signInAsCargoSprint(this.email?.value!, this.password?.value!);
    }

    displaySubscriptionModal(): void {
        this.matDialog.open(TermsOfUseComponent, {
            id: 'termsOfUse',
            width: this.mobileQuery.matches ? 'auto' : '682px',
            maxWidth: '-webkit-fill-available',
            maxHeight: this.mobileQuery.matches ? 'auto' : '519px',
            minHeight: '519px',
            closeOnNavigation: false,
            disableClose: true,
        });
    }

    subscribeMobileSize(): void {
        this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    }

    onEmailChange(): void {
        this.email?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.setNoSSOLogin = false;
        });
    }

    private set setNoSSOLogin(value: boolean) {
        this.noSSOLogin = value;

        if (!value) {
            this.password?.clearValidators();
            this.password?.reset();
        }
    }

    private redirectToSSO(redirectUrl: string): void {
        const source = this.currentFacilityService.getFacilitySource();
        const url = buildSSORedirectUrl(redirectUrl, source);
        const sanitizedUrl: string =
            this.domSanitizer.sanitize(
                SecurityContext.RESOURCE_URL,
                this.domSanitizer.bypassSecurityTrustResourceUrl(url)
            ) ?? '';

        window.open(sanitizedUrl, this.loginSSOTarget);
    }

    private validateSSO(redirectUrl: string | undefined, isMigrated: boolean = false): void {
        if (redirectUrl) {
            this.externalLinksModeService.isExternalLinksDisabled()
                ? this.displaySSODisabledAlert()
                : this.redirectToSSO(redirectUrl);
        } else if (isMigrated) {
            this.keycloakLogin();
        } else {
            const email = this.email?.value || '';

            this.password?.setValidators(Validators.required);
            this.password?.updateValueAndValidity();

            let errors = this.password?.errors;
            if (errors && 'required' in errors) {
                this.email?.setValue(email);
            }
            this.setNoSSOLogin = true;
        }
    }

    private displaySSODisabledAlert(): void {
        Swal.fire({
            icon: 'error',
            title: 'SSO for this portal is disabled.',
            confirmButtonText: 'OK',
        });
    }

    private standardLogin(): Observable<{
        loginResponse: LoginAsCargoSprintUserResponse;
        isTermsOfUseAccepted?: boolean;
    }> {
        return this.login().pipe(
            take(1),
            switchMap((loginResponse: LoginAsCargoSprintUserResponse) => {
                if (loginResponse.authorizationToken && loginResponse.refreshToken) {
                    return this.isTermsOfUseAccepted().pipe(
                        take(1),
                        switchMap((isTermsOfUseAccepted) => {
                            const requestorUser = this.customerService.isRequestorUser();

                            if (requestorUser && !loginResponse?.redirectUrl) {
                                return this.cartBillService.deleteCart(false, false).pipe(
                                    take(1),
                                    map(() => {
                                        return {
                                            loginResponse,
                                            isTermsOfUseAccepted,
                                        };
                                    })
                                );
                            }

                            return of({
                                loginResponse,
                                isTermsOfUseAccepted,
                            });
                        })
                    );
                }

                return of({
                    loginResponse,
                    isTermsOfUseAccepted: false,
                });
            })
        );
    }

    private SSOLogin(email: string): Observable<{
        loginResponse: LoginAsCargoSprintUserResponse;
        isTermsOfUseAccepted?: boolean;
    }> {
        return this.authService.validateEmailSSO(email).pipe(
            take(1),
            map((response) => ({
                loginResponse: { ...response, loginFlow: 'SSO' as const },
            })),
            finalize(() => (this.isLoading = false))
        );
    }

    keycloakLogin(): void {
        this.keycloakHandlerService
            .logoutKeycloak()
            .pipe(
                switchMap(() =>
                    this.keycloakHandlerService.loginKeycloakSession(this.email?.value || '').pipe(take(1))
                ),
                take(1),
                catchError((error: KeycloakException) => {
                    this.keycloakHandlerService.showKeycloakError(error);
                    return EMPTY;
                })
            )
            .subscribe();
    }

    handleKeycloakRedirect(): void {
        if (this.keycloakHandlerService.consumeRedirectFromKeycloak()) {
            return this.subscribeKeycloakSession();
        }
    }

    subscribeKeycloakSession(): void {
        this.isLoading = true;
        this.keycloakHandlerService
            .validateKeycloakSessionAndLogin()
            .pipe(
                take(1),
                catchError((error: KeycloakException) => {
                    this.keycloakHandlerService.showKeycloakError(error);
                    return EMPTY;
                }),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (customToken: CredentialsTokensType | null) => {
                    if (customToken?.authorizationToken && customToken?.refreshToken) {
                        this.sessionService.postAndUpdateConfigTokens(customToken);
                        this.router.navigate(['/']);
                    }
                },
            });
    }
}
